/**
 * Standardized API Response Types V1.0
 * TypeScript interfaces for consistent API response handling
 */

export interface ApiError {
  field?: string;
  code: string;
  message: string;
}

export interface ApiStatus {
  code: number;
  result: 'success' | 'error' | 'warning';
  message?: string;
  timestamp: string;
}

export interface ApiPagination {
  page: number;
  limit: number;
  total: number;
  has_next: boolean;
}

export interface ApiPerformance {
  processing_time_ms: number;
  cache_hit?: boolean;
}

export interface ApiMeta {
  request_id?: string;
  pagination?: ApiPagination;
  performance?: ApiPerformance;
}

export interface StandardApiResponse<T = any> {
  data: T;
  status: ApiStatus;
  meta?: ApiMeta;
  errors?: ApiError[];
}

// Utility type for extracting data from standardized responses
export type ApiData<T extends StandardApiResponse> = T extends StandardApiResponse<infer U> ? U : never;

// Common error codes for consistent error handling
export const ErrorCodes = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  AUTHENTICATION_REQUIRED: 'AUTHENTICATION_REQUIRED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  RESOURCE_CONFLICT: 'RESOURCE_CONFLICT',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  INVALID_REQUEST: 'INVALID_REQUEST',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
} as const;

export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes];

// API Response Validation
export function isStandardApiResponse<T = any>(obj: any): obj is StandardApiResponse<T> {
  return (
    obj &&
    typeof obj === 'object' &&
    'data' in obj &&
    'status' in obj &&
    obj.status &&
    typeof obj.status === 'object' &&
    'code' in obj.status &&
    'result' in obj.status &&
    'timestamp' in obj.status &&
    typeof obj.status.code === 'number' &&
    ['success', 'error', 'warning'].includes(obj.status.result) &&
    typeof obj.status.timestamp === 'string'
  );
}

export function isSuccessResponse<T = any>(response: StandardApiResponse<T>): boolean {
  return response.status.result === 'success';
}

export function isErrorResponse<T = any>(response: StandardApiResponse<T>): boolean {
  return response.status.result === 'error';
}

export function isWarningResponse<T = any>(response: StandardApiResponse<T>): boolean {
  return response.status.result === 'warning';
}

// Error extraction utilities
export function getErrorMessages(response: StandardApiResponse): string[] {
  const messages: string[] = [];

  if (response.status.message) {
    messages.push(response.status.message);
  }

  if ((response as any).errors) {
    messages.push(...(response as any).errors.map(error => error.message));
  }

  return messages;
}

export function getFirstErrorMessage(response: StandardApiResponse, defaultMessage: string = 'An error occurred'): string {
  const messages = getErrorMessages(response);
  return messages.length > 0 ? messages[0] : defaultMessage;
}

export function getValidationErrors(response: StandardApiResponse): Array<{ field?: string; message: string }> {
  if (!(response as any).errors) return [];

  return (response as any).errors
    .filter(error => error.code === ErrorCodes.VALIDATION_ERROR)
    .map(error => ({
      field: error.field,
      message: error.message
    }));
}

// Response builder utilities for testing
export function createMockSuccessResponse<T>(
  data: T,
  message?: string,
  meta?: Partial<ApiMeta>
): StandardApiResponse<T> {
  return {
    data,
    status: {
      code: 200,
      result: 'success',
      message,
      timestamp: new Date().toISOString()
    },
    meta: {
      request_id: crypto.randomUUID(),
      ...meta
    }
  };
}

export function createMockErrorResponse<T = null>(
  statusCode: number,
  message: string,
  errors?: ApiError[],
  data?: T
): StandardApiResponse<T> {
  return {
    data: data || null as T,
    status: {
      code: statusCode,
      result: 'error',
      message,
      timestamp: new Date().toISOString()
    },
    meta: {
      request_id: crypto.randomUUID()
    },
    errors
  };
}